import React, { useState, useEffect } from 'react';
import api from '../../api';
import './ReportingPage.css';

export default function ReportingPage() {
  const [boards, setBoards] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    loadBoards();
  }, []);

  useEffect(() => {
    if (selectedBoard) {
      loadTasks();
    }
  }, [selectedBoard]);

  const loadBoards = async () => {
    try {
      const res = await api.get('/boards');
      setBoards(res.data || []);
    } catch (err) {
      console.error('Failed to load boards:', err);
      alert('Failed to load boards.');
    }
  };

  const loadTasks = async () => {
    if (!selectedBoard) return;
    
    setLoading(true);
    try {
      const tasksRes = await api.get('/tasks');
      const allTasks = tasksRes.data || [];
      // Filter tasks by selected board's team
      const boardTasks = allTasks.filter(task => task.team_id === selectedBoard.team_id);
      
      // Load assignees and subtasks for each task to get hours/points
      const tasksWithAssignees = await Promise.all(
        boardTasks.map(async (task) => {
          try {
            const [assigneesRes, subtasksRes] = await Promise.all([
              api.get(`/assignees/task/${task.id}`),
              api.get(`/subtasks/task/${task.id}`).catch(() => ({ data: [] })) // Subtasks are optional
            ]);
            
            const assignees = assigneesRes.data || [];
            const subtasks = subtasksRes.data || [];
            
            // Calculate main task assignee totals
            const taskHours = assignees.reduce((sum, a) => sum + (parseFloat(a.assigned_hours) || 0), 0);
            const taskPoints = assignees.reduce((sum, a) => sum + (parseInt(a.assigned_points) || 0), 0);
            
            // Calculate subtask assignee totals
            const subtaskHours = subtasks.reduce((sum, subtask) => {
              return sum + ((subtask.assignees || []).reduce((subSum, a) => 
                subSum + (parseFloat(a.assigned_hours) || 0), 0));
            }, 0);
            const subtaskPoints = subtasks.reduce((sum, subtask) => {
              return sum + ((subtask.assignees || []).reduce((subSum, a) => 
                subSum + (parseInt(a.assigned_points) || 0), 0));
            }, 0);
            
            return {
              ...task,
              assignees,
              subtasks,
              total_hours: taskHours + subtaskHours,
              total_points: taskPoints + subtaskPoints
            };
          } catch (err) {
            return { ...task, assignees: [], subtasks: [], total_hours: 0, total_points: 0 };
          }
        })
      );
      
      setTasks(tasksWithAssignees);
    } catch (err) {
      console.error('Failed to load tasks:', err);
      alert('Failed to load tasks.');
    } finally {
      setLoading(false);
    }
  };

  const generateReport = () => {
    if (!selectedBoard) {
      alert('Please select a board first');
      return;
    }

    // Filter tasks by date range
    const filteredTasks = tasks.filter(task => {
      const taskDate = task.created_at ? new Date(task.created_at).toISOString().split('T')[0] : null;
      return taskDate && taskDate >= startDate && taskDate <= endDate;
    });

    // Calculate status counts
    const statusCounts = {};
    filteredTasks.forEach(task => {
      const status = task.status || 'No Status';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    // Calculate type counts
    const typeCounts = {};
    filteredTasks.forEach(task => {
      const type = task.type || 'No Type';
      typeCounts[type] = (typeCounts[type] || 0) + 1;
    });

    // Calculate priority counts
    const priorityCounts = {};
    filteredTasks.forEach(task => {
      const priority = task.priority || 'No Priority';
      priorityCounts[priority] = (priorityCounts[priority] || 0) + 1;
    });

    // Calculate total hours and points
    const totalHours = filteredTasks.reduce((sum, task) => sum + (task.total_hours || 0), 0);
    const totalPoints = filteredTasks.reduce((sum, task) => sum + (task.total_points || 0), 0);
    
    // Calculate hours/points by status
    const hoursByStatus = {};
    const pointsByStatus = {};
    filteredTasks.forEach(task => {
      const status = task.status || 'No Status';
      hoursByStatus[status] = (hoursByStatus[status] || 0) + (task.total_hours || 0);
      pointsByStatus[status] = (pointsByStatus[status] || 0) + (task.total_points || 0);
    });

    // Calculate hours/points by type
    const hoursByType = {};
    const pointsByType = {};
    filteredTasks.forEach(task => {
      const type = task.type || 'No Type';
      hoursByType[type] = (hoursByType[type] || 0) + (task.total_hours || 0);
      pointsByType[type] = (pointsByType[type] || 0) + (task.total_points || 0);
    });

    // Calculate hours/points by priority
    const hoursByPriority = {};
    const pointsByPriority = {};
    filteredTasks.forEach(task => {
      const priority = task.priority || 'No Priority';
      hoursByPriority[priority] = (hoursByPriority[priority] || 0) + (task.total_hours || 0);
      pointsByPriority[priority] = (pointsByPriority[priority] || 0) + (task.total_points || 0);
    });

    setReportData({
      board: selectedBoard,
      dateRange: { start: startDate, end: endDate },
      totalTasks: filteredTasks.length,
      totalHours,
      totalPoints,
      statusCounts,
      typeCounts,
      priorityCounts,
      hoursByStatus,
      pointsByStatus,
      hoursByType,
      pointsByType,
      hoursByPriority,
      pointsByPriority,
      tasks: filteredTasks
    });
  };

  return (
    <div className="reporting-page">
      <h2>Reports</h2>

      <div className="report-filters-section">
        <div className="filter-row">
          <div className="filter-field">
            <label htmlFor="board-select">Board:</label>
            <select
              id="board-select"
              className="report-select"
              value={selectedBoard?.id || ''}
              onChange={(e) => {
                const boardId = parseInt(e.target.value);
                const board = boards.find(b => b.id === boardId);
                setSelectedBoard(board || null);
                setReportData(null);
              }}
            >
              <option value="">Select a board...</option>
              {boards.map((board) => (
                <option key={board.id} value={board.id}>
                  {board.name} {board.team_name ? `(${board.team_name})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-field">
            <label htmlFor="start-date">Start Date:</label>
            <input
              id="start-date"
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setReportData(null);
              }}
              className="report-date-input"
            />
          </div>

          <div className="filter-field">
            <label htmlFor="end-date">End Date:</label>
            <input
              id="end-date"
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setReportData(null);
              }}
              className="report-date-input"
            />
          </div>

          <button
            className="generate-report-btn"
            onClick={generateReport}
            disabled={!selectedBoard || loading}
          >
            Generate Report
          </button>
        </div>
      </div>

      {loading && (
        <div className="loading-message">Loading tasks...</div>
      )}

      {reportData && (
        <div className="report-results">
          <div className="report-header">
            <h3>{reportData.board.name} Report</h3>
            <div className="report-date-range">
              {new Date(reportData.dateRange.start).toLocaleDateString()} - {new Date(reportData.dateRange.end).toLocaleDateString()}
            </div>
          </div>

          <div className="report-summary">
            <div className="summary-card">
              <div className="summary-label">Total Tasks</div>
              <div className="summary-value">{reportData.totalTasks}</div>
            </div>
            {reportData.totalHours > 0 && (
              <div className="summary-card">
                <div className="summary-label">Total Hours</div>
                <div className="summary-value">{reportData.totalHours.toFixed(1)}</div>
              </div>
            )}
            {reportData.totalPoints > 0 && (
              <div className="summary-card">
                <div className="summary-label">Total Points</div>
                <div className="summary-value">{reportData.totalPoints}</div>
              </div>
            )}
          </div>

          <div className="report-sections">
            {/* Status Counts */}
            <div className="report-section">
              <h4>Status Breakdown</h4>
              <div className="count-grid">
                {Object.entries(reportData.statusCounts).map(([status, count]) => (
                  <div key={status} className="count-item">
                    <div className="count-item-header">
                      <span className="count-label">{status}</span>
                      <span className="count-value">{count}</span>
                    </div>
                    {(reportData.hoursByStatus[status] > 0 || reportData.pointsByStatus[status] > 0) && (
                      <div className="count-item-metrics">
                        {reportData.hoursByStatus[status] > 0 && (
                          <span className="metric">{reportData.hoursByStatus[status].toFixed(1)}h</span>
                        )}
                        {reportData.hoursByStatus[status] > 0 && reportData.pointsByStatus[status] > 0 && (
                          <span className="metric-separator">•</span>
                        )}
                        {reportData.pointsByStatus[status] > 0 && (
                          <span className="metric">{reportData.pointsByStatus[status]}pts</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Type Counts */}
            <div className="report-section">
              <h4>Type Breakdown</h4>
              <div className="count-grid">
                {Object.entries(reportData.typeCounts).map(([type, count]) => (
                  <div key={type} className="count-item">
                    <div className="count-item-header">
                      <span className="count-label">{type}</span>
                      <span className="count-value">{count}</span>
                    </div>
                    {(reportData.hoursByType[type] > 0 || reportData.pointsByType[type] > 0) && (
                      <div className="count-item-metrics">
                        {reportData.hoursByType[type] > 0 && (
                          <span className="metric">{reportData.hoursByType[type].toFixed(1)}h</span>
                        )}
                        {reportData.hoursByType[type] > 0 && reportData.pointsByType[type] > 0 && (
                          <span className="metric-separator">•</span>
                        )}
                        {reportData.pointsByType[type] > 0 && (
                          <span className="metric">{reportData.pointsByType[type]}pts</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Priority Counts */}
            <div className="report-section">
              <h4>Priority Breakdown</h4>
              <div className="count-grid">
                {Object.entries(reportData.priorityCounts).map(([priority, count]) => (
                  <div key={priority} className="count-item">
                    <div className="count-item-header">
                      <span className="count-label">{priority}</span>
                      <span className="count-value">{count}</span>
                    </div>
                    {(reportData.hoursByPriority[priority] > 0 || reportData.pointsByPriority[priority] > 0) && (
                      <div className="count-item-metrics">
                        {reportData.hoursByPriority[priority] > 0 && (
                          <span className="metric">{reportData.hoursByPriority[priority].toFixed(1)}h</span>
                        )}
                        {reportData.hoursByPriority[priority] > 0 && reportData.pointsByPriority[priority] > 0 && (
                          <span className="metric-separator">•</span>
                        )}
                        {reportData.pointsByPriority[priority] > 0 && (
                          <span className="metric">{reportData.pointsByPriority[priority]}pts</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {!reportData && selectedBoard && !loading && (
        <div className="no-report-message">
          Select a date range and click "Generate Report" to view statistics
        </div>
      )}
    </div>
  );
}

